<template>
  <modal-component
    :is-displayed="isCreateModalOpened"
    :no-padding="true"
    :title="$t('ICDManagement.CreateTitle')"
    @toggle-modal="$emit('toggle-modal')"
  >
    <new-mac-form
      v-if="isNewMacModalOpened"
      :is-new-mac-modal-opened="isNewMacModalOpened"
      :mac="sensor.mac"
      @toggle-modal="isNewMacModalOpened = false"
      @new-mac-created="newMacCreated"
      @get-mac="getNewMac"
    />
    <new-customer-form
      v-if="isNewCustomerModalOpened"
      :is-new-customer-modal-opened="isNewCustomerModalOpened"
      @toggle-modal="isNewCustomerModalOpened = false"
      @get-customer="getNewCustomer"
      :refresh-data="getCustomers"
    />
    <reassign-modal
      v-if="isReassignModalOpened"
      :is-reassign-modal-opened="isReassignModalOpened"
      :customer="customerByMac"
      @toggle-modal="isReassignModalOpened = false"
      @reassign-action="handleReassign"
    />
    <form-wrapper :is-form-in-modal="true">
      <form @submit.prevent="handleSubmit">
        <form-divider>
          <div class="flex items-center gap-2">
            <text-box-mask
              :label="$t('ICDManagement.MAC')"
              :vModel="sensor.mac"
              :maskRules="rules"
              :inputAttr="{
                'aria-label': 'Mask',
                style: 'text-transform: uppercase',
              }"
              :showClearButton="false"
              :value="sensor.mac"
              mask="XX:XX:XX:XX:XX:XX"
              valueChangeEvent="keyup"
              :is-success="isMacAssigned"
              :success-message="'Identification tag is already assigned'"
              @value-changed="onValueChange"
            />
            <div :class="isMacAssigned ? 'pb-1' : 'pb-3 pt-10'">
              <the-button
                :icon="['fas', 'add']"
                type="button"
                :disabled="isMacInputDisabled"
                @execute-method="toggleNewMacModal"
              />
            </div>
          </div>
          <div class="relative items-center py-4">
            <label class="whitespace-nowrap text-sm font-medium not-italic text-gray-800">{{
              $t("ICDManagement.CreateLabelTag")
            }}</label>
            <div class="flex items-center justify-center gap-2">
              <small-loader v-if="isCustomersListLoading" height="h-8" width="w-8" />
              <v-select
                v-else
                v-model="customer.id"
                :class="`mt-1 w-full`"
                :options="Customers"
                :reduce="(customer: ICDCustomer) => customer.id"
                label="name"
              />
              <div class="pt-1">
                <the-button
                  :icon="['fas', 'add']"
                  type="button"
                  :disabled="false"
                  @execute-method="toggleNewCustomerModal"
                />
              </div>
            </div>
          </div>
        </form-divider>
        <button-wrapper :is-submit-loading="isSubmitLoading" />
      </form>
    </form-wrapper>
  </modal-component>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import ModalComponent from "@/core/Components/Modal.vue";
import FormWrapper from "@/core/Components/FormElements/FormWrapper.vue";
import NewMacForm from "./AdditionalModals/NewMacForm.vue";
import NewCustomerForm from "./AdditionalModals/NewCustomerForm.vue";
import FormDivider from "@/core/Components/FormElements/FormDivider.vue";
import SmallLoader from "@/core/Components/SmallLoader.vue";
import VSelect from "vue-select";
import ButtonWrapper from "@/core/Components/FormElements/ButtonWrapper.vue";
import TheButton from "@/core/Components/TheButton.vue";
import { useToast } from "vue-toastification";
import {
  ErrorMessageFormatter,
  formatMacAddress,
  formatMacAddressBackToString,
  isString,
} from "@/core/Services/GlobalFunctions";
import useValidate from "@vuelidate/core";
import { maxLength, minLength, required } from "@vuelidate/validators";
import TextBoxMask from "@/core/Components/FormElements/TextBoxMask.vue";
import ReassignModal from "./AdditionalModals/ReassignModal.vue";

import "devextreme/dist/css/dx.light.css";

import { DxTextBox } from "devextreme-vue/text-box";
import { AssignIcd, ICD, ICDCustomer, ICDMac, ICDNewCustomer } from "../Services/ICDManagementTypes";
import IcdService from "../Services/ICDManagementService";

export default defineComponent({
  components: {
    ButtonWrapper,
    VSelect,
    SmallLoader,
    FormDivider,
    FormWrapper,
    ModalComponent,
    TextBoxMask,
    DxTextBox,
    TheButton,
    NewMacForm,
    NewCustomerForm,
    ReassignModal,
  },
  props: {
    isCreateModalOpened: Boolean,
    refreshData: {
      type: Function,
      required: true,
    },
    icdData: {
      type: Object,
      required: true,
    },
  },
  emits: ["toggle-modal", "new-mac-created"],
  data() {
    return {
      toast: useToast(),
      v$: useValidate(),
      icdService: new IcdService(),
      isDataSourceLoading: false,
      isCustomersListLoading: false,
      isSubmitLoading: false,
      isMacInputDisabled: true,
      isCustomerInputDisabled: true,
      isNewMacModalOpened: false,
      isNewCustomerModalOpened: false,
      isReassignModalOpened: false,
      isMacAssigned: false,
      validationErrors: [] as any,
      rules: {
        X: /[0-9A-Fa-f]/,
      },
      Customers: [] as ICDCustomer[],
      sensor: {
        mac: "XX:XX:XX:XX:XX:XX",
      } as ICDMac,
      customerByMac: {
        id: null,
        name: "",
      } as ICDCustomer,
      customer: {
        id: null,
        name: "",
      } as ICDCustomer,
      icd: {
        mac: "",
        customerId: null,
      } as AssignIcd,
    };
  },
  validations() {
    return {
      sensor: {
        mac: {
          required,
        },
      },
      customer: {
        required,
      },
    };
  },

  async created() {
    try {
      if (!this.isCreateModalOpened) {
        this.sensor.mac = "";
        this.customer.id = null;
      }
      await this.getCustomers();
    } catch (error: any) {
      if (!isString(error.response.data)) {
        this.toast.error(ErrorMessageFormatter(error));
        return;
      }
      this.toast.error(error.response.data);
    }
  },
  methods: {
    async getCustomers() {
      this.isDataSourceLoading = true;
      try {
        this.Customers = await this.icdService.getCustomers({
          pageNumber: 1,
          pageSize: 100000,
        });
      } catch (error: any) {
        if (!isString(error.response.data)) {
          this.toast.error(ErrorMessageFormatter(error));
          return;
        }
        this.toast.error(error.response.data);
      } finally {
        this.isDataSourceLoading = false;
      }
    },

    async handleSubmit() {
      this.isSubmitLoading = true;
      try {
        if (this.sensor.mac === "") {
          this.sensor.mac = "XX:XX:XX:XX:XX:XX";
        }

        this.icd = {
          mac: this.sensor.mac,
          customerId: this.customer.id,
        };

        if (this.isMacAssigned) {
          this.toggleReassignModal();
          return;
        }

        await this.icdService.assignIcd(this.icd);
        this.sensor.mac = "";
        this.customer.id = null;
        this.$emit("toggle-modal");
        this.toast.success("ICD Assigned successfully!");
        await this.refreshData();
      } catch (error: any) {
        if (!isString(error.response.data)) {
          this.toast.error(ErrorMessageFormatter(error));
          return;
        }
        this.toast.error(error.response.data);
      } finally {
        this.isSubmitLoading = false;
      }
    },

    async handleReassign() {
      this.isSubmitLoading = true;
      try {
        this.icd = {
          mac: this.sensor.mac,
          customerId: this.customer.id,
        };
        await this.icdService.assignIcd(this.icd);

        this.sensor.mac = "";
        this.customer.id = null;
        this.isMacAssigned = false;
        this.isReassignModalOpened = false;

        this.$emit("toggle-modal");
        this.toast.success("ICD Reassigned successfully!");
        await this.refreshData();
      } catch (error: any) {
        if (!isString(error.response.data)) {
          this.toast.error(ErrorMessageFormatter(error));
          return;
        }
        this.toast.error(error.response.data);
      } finally {
        this.isSubmitLoading = false;
      }
    },

    toggleNewMacModal() {
      this.isNewMacModalOpened = !this.isNewMacModalOpened;
    },
    toggleNewCustomerModal() {
      this.isNewCustomerModalOpened = !this.isNewCustomerModalOpened;
    },

    toggleReassignModal() {
      this.isReassignModalOpened = !this.isReassignModalOpened;
    },

    getNewMac(mac: string) {
      this.sensor = {
        mac: mac,
      };
    },

    async getNewCustomer(customer: ICDNewCustomer) {
      this.customer = {
        id: customer.data,
        name: customer.name,
      };
      await this.getCustomers();
    },

    newMacCreated() {
      this.$emit("new-mac-created");
    },

    handleMacAlreadyAssignedCheck() {
      this.isSubmitLoading = true;
      try {
        this.isMacAssigned = this.icdData.some(
          (icd: { mac: string; customerId: any }) => icd.mac === this.sensor.mac && icd.customerId
        );

        if (this.isMacAssigned) {
          const customerData: ICD = this.icdData.find(
            (x: { mac: string; customer: { id: number | null; name: string } }) => x.mac === this.sensor.mac
          );
          const { id, name } = customerData!.customer!;
          if (customerData && customerData.customer) {
            this.customer.id = id;
            this.customer.name = name;
            this.customerByMac.id = id;
            this.customerByMac.name = name;
          } else {
            this.customer.id = null;
            this.customer.name = "";
            this.customerByMac.id = null;
            this.customerByMac.name = "";
          }
        } else {
          this.isMacAssigned = false;
        }
      } catch (error: any) {
        if (!isString(error.response.data)) {
          this.toast.error(ErrorMessageFormatter(error));
          return;
        }
        this.toast.error(error.response.data);
      } finally {
        this.isSubmitLoading = false;
      }
    },

    onValueChange(e: any) {
      try {
        const newValue = e.value.toUpperCase();
        const rawValue = formatMacAddressBackToString(newValue);
        const formattedMac = formatMacAddress(rawValue);

        if (this.sensor.mac !== formattedMac) {
          this.sensor.mac = formattedMac;
        }
        this.customer.id = null;
        this.isMacInputDisabled = this.macExists;
        this.handleMacAlreadyAssignedCheck();
      } catch (error: any) {
        if (!isString(error.response.data)) {
          this.toast.error(ErrorMessageFormatter(error));
        } else {
          this.toast.error(error.response.data);
        }
        throw error;
      }
    },
  },
  computed: {
    macExists(): boolean {
      return this.icdData.some((data: { mac: string }) => data.mac === this.sensor.mac) || this.sensor.mac === "";
    },
  },
});
</script>

<style scoped></style>
